// LocationsTab.js
// Manages office locations: add, edit, activate/deactivate.

import React, { useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import { formatPhone, styles } from './settingsShared';

export default function LocationsTab({ locations, onRefresh }) {
  const emptyForm = {
    name: '', address_line1: '', address_line2: '',
    city: '', state: '', zip_code: '', phone: '', fax: '',
  };

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openAdd = () => {
    setEditingId(null); setForm(emptyForm); setError(''); setShowForm(true);
  };

  const openEdit = (loc) => {
    setEditingId(loc.id);
    setForm({
      name: loc.name || '', address_line1: loc.address_line1 || '',
      address_line2: loc.address_line2 || '', city: loc.city || '',
      state: loc.state || '', zip_code: loc.zip_code || '',
      phone: loc.phone || '', fax: loc.fax || '',
    });
    setError(''); setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false); setEditingId(null); setForm(emptyForm); setError('');
  };

  const handlePhone = (field, value) =>
    setForm(f => ({ ...f, [field]: formatPhone(value) }));

  const handleSave = async () => {
    if (!form.name) { setError('Location name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (editingId) {
        await api.patch(`/locations/${editingId}`, form);
      } else {
        await api.post('/locations/', form);
      }
      cancelForm(); onRefresh();
    } catch {
      setError('Failed to save location.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (loc) => {
    try {
      await api.patch(`/locations/${loc.id}`, { is_active: !loc.is_active });
      onRefresh();
    } catch {}
  };

  const cityState = (loc) => {
    if (loc.city && loc.state) return `${loc.city}, ${loc.state}`;
    return loc.city || loc.state || '—';
  };

  const FORM_FIELDS = [
    { label: 'Location Name *', field: 'name' },
    { label: 'Phone', field: 'phone', phone: true },
    { label: 'Fax', field: 'fax', phone: true },
    { label: 'Address', field: 'address_line1' },
    { label: 'Address Line 2', field: 'address_line2' },
    { label: 'City', field: 'city' },
  ];

  return (
    <div>
      <div style={styles.tabHeader}>
        <h2 style={styles.sectionTitle}>Office Locations</h2>
        <Button onClick={showForm ? cancelForm : openAdd} variant={showForm ? 'secondary' : 'primary'}>
          {showForm ? 'Cancel' : '+ Add Location'}
        </Button>
      </div>

      {showForm && (
        <div style={styles.form}>
          <div style={styles.formTitle}>{editingId ? 'Edit Location' : 'New Location'}</div>
          {error && <div style={styles.errorText}>{error}</div>}
          <div style={styles.formGrid}>
            {FORM_FIELDS.map(({ label, field, phone }) => (
              <div key={field} style={styles.fieldGroup}>
                <label style={styles.label}>{label}</label>
                <input
                  style={styles.input}
                  value={form[field]}
                  onChange={e =>
                    phone
                      ? handlePhone(field, e.target.value)
                      : setForm({ ...form, [field]: e.target.value })
                  }
                  placeholder={phone ? '(xxx) xxx-xxxx' : ''}
                />
              </div>
            ))}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>State</label>
              <input
                style={{ ...styles.input, maxWidth: '80px' }}
                value={form.state}
                maxLength={2}
                onChange={e => setForm({ ...form, state: e.target.value.toUpperCase() })}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>ZIP</label>
              <input
                style={{ ...styles.input, maxWidth: '120px' }}
                value={form.zip_code}
                onChange={e => setForm({ ...form, zip_code: e.target.value })}
              />
            </div>
          </div>
          <div style={styles.formActions}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Location'}
            </Button>
            <Button variant="secondary" onClick={cancelForm}>Cancel</Button>
          </div>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            {['Name', 'Phone', 'Fax', 'City, State', 'Status', 'Actions'].map(h => (
              <th key={h} style={styles.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {locations.length === 0 && (
            <tr><td colSpan={6} style={styles.emptyRow}>No locations added yet.</td></tr>
          )}
          {locations.map(loc => (
            <tr key={loc.id} style={styles.tr}>
              <td style={styles.td}>{loc.name}</td>
              <td style={styles.td}>{loc.phone || '—'}</td>
              <td style={styles.td}>{loc.fax || '—'}</td>
              <td style={styles.td}>{cityState(loc)}</td>
              <td style={styles.td}>
                <span style={loc.is_active ? styles.badgeActive : styles.badgeInactive}>
                  {loc.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={styles.td}>
                <div style={styles.actionGroup}>
                  <Button variant="neutral" size="sm" onClick={() => openEdit(loc)}>
                    Edit
                  </Button>
                  <Button
                    variant={loc.is_active ? 'caution' : 'neutral'}
                    size="sm"
                    onClick={() => toggleActive(loc)}
                  >
                    {loc.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}